import Phaser from "phaser";
import { COLORS, TEXT_STYLES } from "../constants";
import { registerAutomationTarget } from "../automation/automationTags";
import { PrimitiveAvatar } from "../avatars/PrimitiveAvatar";
import type { PrimitiveBlueprint } from "../avatars/types";

type CharacterCardConfig = {
  width: number;
  height: number;
  characterName: string;
  description: string;
  stats: {
    maxHp: number;
    atk: number;
    armor: number;
  };
  startingSkills: string[];
  sprite?: PrimitiveBlueprint;
  automationName?: string;
  onClick: () => void;
};

export class CharacterCard extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Rectangle;
  private isSelected = false;
  private isEnabled = true;
  private isHovering = false;
  private normalStrokeColor = COLORS.buttonStroke;
  private selectedStrokeColor = 0xfbbf24; // amber-400
  private hoverGlow?: Phaser.GameObjects.Rectangle;
  private avatar?: PrimitiveAvatar;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    {
      width,
      height,
      characterName,
      description,
      stats,
      startingSkills,
      sprite,
      onClick,
      automationName,
    }: CharacterCardConfig
  ) {
    super(scene, x, y);

    scene.add.existing(this);
    this.setScrollFactor(0);

    // Hover glow effect (behind background)
    this.hoverGlow = scene.add.rectangle(width / 2, height / 2, width + 8, height + 8, 0xffffff, 0);
    this.hoverGlow.setOrigin(0.5);
    this.hoverGlow.setScrollFactor(0);
    this.add(this.hoverGlow);

    // Background rectangle
    this.background = scene.add.rectangle(width / 2, height / 2, width, height, COLORS.buttonFill);
    this.background.setOrigin(0.5);
    this.background.setStrokeStyle(3, this.normalStrokeColor);
    this.background.setScrollFactor(0);
    this.add(this.background);

    // Avatar/sprite on the left
    if (sprite) {
      const avatarSize = Math.min(height - 24, 70);
      const avatarScale = avatarSize / sprite.designSize.w;
      this.avatar = new PrimitiveAvatar(scene, sprite, { scale: avatarScale });
      this.avatar.setPosition(avatarSize / 2 + 12, height / 2);
      this.add(this.avatar.getRoot());
    }

    // Text content area (to the right of avatar)
    const textX = sprite ? 96 : 16;
    const textWidth = width - textX - 16;

    // Character name
    const nameText = scene.add
      .text(textX, 16, characterName, {
        ...TEXT_STYLES.button,
        fontSize: "24px",
      })
      .setOrigin(0, 0)
      .setScrollFactor(0);
    this.add(nameText);

    // Description
    const descText = scene.add
      .text(textX, 46, description, {
        ...TEXT_STYLES.body,
        fontSize: "16px",
        wordWrap: { width: textWidth },
      })
      .setOrigin(0, 0)
      .setScrollFactor(0);
    this.add(descText);

    // Stats display
    const statsY = height - 52;
    const statText = `HP: ${stats.maxHp} | ATK: ${stats.atk} | ARM: ${stats.armor}`;
    const statsDisplay = scene.add
      .text(textX, statsY, statText, {
        ...TEXT_STYLES.body,
        fontSize: "16px",
        color: COLORS.textPrimary,
      })
      .setOrigin(0, 0)
      .setScrollFactor(0);
    this.add(statsDisplay);

    // Starting skills
    const skillsY = height - 28;
    const skillsText = `Skills: ${startingSkills.join(", ")}`;
    const skillsDisplay = scene.add
      .text(textX, skillsY, skillsText, {
        ...TEXT_STYLES.body,
        fontSize: "14px",
        color: COLORS.textMuted,
      })
      .setOrigin(0, 0)
      .setScrollFactor(0);
    this.add(skillsDisplay);

    this.setSize(width, height);
    this.setupInteractions(onClick, width, height);
    this.registerAutomation(automationName ?? `character-${characterName.toLowerCase()}`);
  }

  private setupInteractions(onClick: () => void, width: number, height: number) {
    const hitArea = new Phaser.Geom.Rectangle(0, 0, width, height);

    this.background.setInteractive({
      hitArea,
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });

    // Hover effects
    this.background.on("pointerover", () => {
      if (!this.isEnabled) return;
      this.isHovering = true;
      this.hoverVisual();
    });

    this.background.on("pointerout", () => {
      if (!this.isEnabled) return;
      this.isHovering = false;
      this.unhoverVisual();
    });

    this.background.on("pointerdown", () => {
      if (!this.isEnabled) return;
      this.pressVisual();
    });

    this.background.on("pointerup", () => {
      if (!this.isEnabled) return;
      onClick();
      this.releaseVisual();
    });

    this.background.on("pointerupoutside", () => {
      if (!this.isEnabled) return;
      this.releaseVisual();
    });
  }

  private hoverVisual() {
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }

    // Subtle glow effect on hover
    if (this.hoverGlow) {
      this.scene.tweens.killTweensOf(this.hoverGlow);
      this.scene.tweens.add({
        targets: this.hoverGlow,
        alpha: 0.15,
        duration: 200,
        ease: "Sine.easeOut",
      });
    }

    // Subtle scale up
    this.scene.tweens.killTweensOf(this.background);
    this.scene.tweens.add({
      targets: this.background,
      scale: 1.02,
      duration: 200,
      ease: "Sine.easeOut",
    });
  }

  private unhoverVisual() {
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }

    // Remove glow
    if (this.hoverGlow) {
      this.scene.tweens.killTweensOf(this.hoverGlow);
      this.scene.tweens.add({
        targets: this.hoverGlow,
        alpha: 0,
        duration: 200,
        ease: "Sine.easeOut",
      });
    }

    // Return to normal scale
    this.scene.tweens.killTweensOf(this.background);
    this.scene.tweens.add({
      targets: this.background,
      scale: 1,
      duration: 200,
      ease: "Sine.easeOut",
    });
  }

  private pressVisual() {
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }
    this.scene.tweens.killTweensOf(this.background);
    this.background.setScale(0.98);
    this.setAlpha(0.9);
  }

  private releaseVisual() {
    if (!this.scene || !this.scene.tweens || !this.active) {
      return;
    }
    this.scene.tweens.killTweensOf(this.background);

    // Return to hover state if still hovering, otherwise normal
    const targetScale = this.isHovering ? 1.02 : 1;

    this.scene.tweens.add({
      targets: this.background,
      scale: targetScale,
      duration: 140,
      ease: "Sine.easeOut",
    });
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      duration: 140,
      ease: "Sine.easeOut",
    });
  }

  setSelected(selected: boolean) {
    this.isSelected = selected;
    const strokeColor = selected ? this.selectedStrokeColor : this.normalStrokeColor;
    const strokeWidth = selected ? 4 : 3;
    this.background.setStrokeStyle(strokeWidth, strokeColor);

    // Add subtle glow effect when selected
    if (selected) {
      this.background.setFillStyle(COLORS.buttonFill, 0.9);
    } else {
      this.background.setFillStyle(COLORS.buttonFill, 1);
    }
  }

  getIsSelected(): boolean {
    return this.isSelected;
  }

  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    if (enabled) {
      this.background.setInteractive({ useHandCursor: true });
    } else {
      this.background.disableInteractive();
    }
    this.setAlpha(enabled ? 1 : 0.55);
    if (this.avatar) {
      this.avatar.getRoot().setAlpha(enabled ? 1 : 0.55);
    }
  }

  private registerAutomation(name: string) {
    const cleanup = registerAutomationTarget(this.scene, {
      type: "button",
      name,
      getBounds: () => this.getBounds(),
      getText: () => name,
    });

    this.once(Phaser.GameObjects.Events.DESTROY, cleanup);
  }
}
